'use client';

import type { Address } from 'viem';
import { encodePacked, keccak256, getAddress, toBytes, pad, concat, slice, isBytes, type Hex, type ByteArray } from 'viem';
import type { ChainType } from '@/constants/chain-specific';
import type { DexConfig } from '@/constants/project-specific/farms/types';
import { pinnedTokens } from '@/constants/project-specific/tokens';

/**
 * Token info for pair computation
 */
interface TokenInfo {
    address: Address;
    symbol?: string;
}

/**
 * Computed pair result
 */
export interface ComputedPair {
    pairAddress: Address;
    token0: TokenInfo;
    token1: TokenInfo;
    dexSlug: string;
}

/**
 * Sort two token addresses to get consistent pair addresses
 * Token with lower address value comes first
 */
function sortTokens(tokenA: Address, tokenB: Address): [Address, Address] {
    const addrA = tokenA.toLowerCase();
    const addrB = tokenB.toLowerCase();
    return addrA < addrB ? [tokenA, tokenB] : [tokenB, tokenA];
}

/**
 * Compute CREATE2 pair address for a UniswapV2-style DEX
 * 
 * Formula: keccak256(0xff ++ factoryAddress ++ salt ++ initCodeHash)[12:]
 * where salt = keccak256(abi.encodePacked(token0, token1))
 */
export function computePairAddress(
    factoryAddress: Address,
    tokenA: Address,
    tokenB: Address,
    initCodeHash: Hex
): Address {
    const [token0, token1] = sortTokens(tokenA, tokenB);
    
    // Compute salt = keccak256(abi.encodePacked(token0, token1))
    const salt = keccak256(encodePacked(['address', 'address'], [token0, token1]));
    
    // Compute CREATE2 address
    const from = toBytes(getAddress(factoryAddress));
    const saltBytes = pad(isBytes(salt) ? salt : toBytes(salt as Hex), { size: 32 }) as ByteArray;
    
    const create2Address = getAddress(
        slice(
            keccak256(concat([toBytes('0xff'), from, saltBytes, toBytes(initCodeHash)])),
            12
        )
    );
    
    return create2Address;
}

/**
 * Get all token pair combinations from pinnedTokens config
 * Returns pairs of [tokenA, tokenB] addresses
 */
export function getTokenPairCombinations(chainId: ChainType): Array<[TokenInfo, TokenInfo]> {
    const chainTokens = pinnedTokens[chainId as keyof typeof pinnedTokens];
    
    if (!chainTokens) {
        console.log(`[ComputePairs] No pinned tokens for chain ${chainId}`);
        return [];
    }
    
    const commonBases = chainTokens.commonBases || [];
    const basePairs = chainTokens.basePairs || [];
    
    // Combine all tokens to check
    const allTokens = [...commonBases, ...basePairs];
    
    // Remove duplicates by address
    const uniqueTokens = allTokens.reduce<TokenInfo[]>((acc, token) => {
        if (!token?.address) return acc;
        const exists = acc.some(t => t.address.toLowerCase() === token.address.toLowerCase());
        if (!exists) {
            acc.push({
                address: token.address as Address,
                symbol: token.symbol,
            });
        }
        return acc;
    }, []);
    
    console.log(`[ComputePairs] Found ${uniqueTokens.length} unique tokens for chain ${chainId}`);
    
    // Generate all pair combinations
    const pairs: Array<[TokenInfo, TokenInfo]> = [];
    
    for (let i = 0; i < uniqueTokens.length; i++) {
        for (let j = i + 1; j < uniqueTokens.length; j++) {
            pairs.push([uniqueTokens[i], uniqueTokens[j]]);
        }
    }
    
    console.log(`[ComputePairs] Generated ${pairs.length} unique pair combinations`);
    
    return pairs;
}

/**
 * Compute all pair addresses for a DEX using CREATE2
 * Only works for DEXes with a codeHash configured
 */
export function computeAllPairAddresses(
    dexSlug: string,
    dexConfig: DexConfig,
    chainId: ChainType
): ComputedPair[] {
    // Check if DEX has codeHash for CREATE2 computation
    const codeHash = dexConfig.factory?.codeHash;
    const factoryAddress = dexConfig.factory?.address;
    
    if (!codeHash || !factoryAddress) {
        console.log(`[ComputePairs] ${dexSlug} doesn't have codeHash or factory, skipping CREATE2 computation`);
        return [];
    }
    
    const tokenPairs = getTokenPairCombinations(chainId);
    
    if (tokenPairs.length === 0) {
        return [];
    }
    
    const computedPairs: ComputedPair[] = [];
    
    for (const [tokenA, tokenB] of tokenPairs) {
        try {
            const pairAddress = computePairAddress(
                factoryAddress as Address,
                tokenA.address,
                tokenB.address,
                codeHash as Hex
            );
            
            // Sort tokens for consistent ordering
            const [sorted0, sorted1] = sortTokens(tokenA.address, tokenB.address);
            const token0 = tokenA.address.toLowerCase() === sorted0.toLowerCase() ? tokenA : tokenB;
            const token1 = tokenA.address.toLowerCase() === sorted0.toLowerCase() ? tokenB : tokenA;
            
            computedPairs.push({
                pairAddress,
                token0,
                token1,
                dexSlug,
            });
        } catch (error) {
            console.error(`[ComputePairs] Error computing pair for ${tokenA.symbol}/${tokenB.symbol}:`, error);
        }
    }
    
    console.log(`[ComputePairs] Computed ${computedPairs.length} pair addresses for ${dexSlug}`);
    
    return computedPairs;
}

/**
 * Cache for computed pair addresses to avoid recomputation
 */
const COMPUTED_PAIRS_CACHE = new Map<string, ComputedPair[]>();

/**
 * Get computed pairs with caching
 */
export function getCachedComputedPairs(
    dexSlug: string,
    dexConfig: DexConfig,
    chainId: ChainType
): ComputedPair[] {
    const cacheKey = `${chainId}-${dexSlug}`;
    
    if (!COMPUTED_PAIRS_CACHE.has(cacheKey)) {
        const pairs = computeAllPairAddresses(dexSlug, dexConfig, chainId);
        COMPUTED_PAIRS_CACHE.set(cacheKey, pairs);
    }
    
    return COMPUTED_PAIRS_CACHE.get(cacheKey) || [];
}

/**
 * Clear the computed pairs cache
 */
export function clearComputedPairsCache(): void {
    COMPUTED_PAIRS_CACHE.clear();
}
